/**
 * Custom hook for checking appointment availability
 */

import { useState, useCallback } from 'react';
import type { AvailabilityCheckResponse, AvailabilitySlot } from '@/types';
import { checkAvailability } from '@/services/widgetApi';

interface UseAvailabilityParams {
  practiceSlug: string;
}

interface UseAvailabilityResult {
  slots: AvailabilitySlot[];
  loading: boolean;
  error: string | null;
  checkSlots: (params: {
    serviceId: string;
    clinicianId: string;
    locationId: string;
    date: string; // YYYY-MM-DD
  }) => Promise<void>;
  clearSlots: () => void;
}

/**
 * Hook to check appointment availability
 */
export function useAvailability({ practiceSlug }: UseAvailabilityParams): UseAvailabilityResult {
  const [slots, setSlots] = useState<AvailabilitySlot[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const checkSlots = useCallback(
    async (params: {
      serviceId: string;
      clinicianId: string;
      locationId: string;
      date: string;
    }) => {
      // Validate parameters
      if (!params.serviceId || !params.clinicianId || !params.locationId || !params.date) {
        setError('All parameters are required to check availability');
        return;
      }

      if (!practiceSlug) {
        setError('Practice slug is required');
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response: AvailabilityCheckResponse = await checkAvailability(practiceSlug, {
          availability_check: {
            practice_service_id: params.serviceId,
            clinician_id: params.clinicianId,
            location_id: params.locationId,
            requested_date: params.date,
            appointment_type: 'individual',
          },
        });

        if (response.success && response.available) {
          setSlots(response.slots);
        } else {
          setSlots([]);
          if (!response.available) {
            setError('No available slots for the selected date');
          }
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to check availability';
        setError(errorMessage);
        setSlots([]);
        console.error('Error in useAvailability:', err);
      } finally {
        setLoading(false);
      }
    },
    [practiceSlug],
  );

  const clearSlots = useCallback(() => {
    setSlots([]);
    setError(null);
  }, []);

  return {
    slots,
    loading,
    error,
    checkSlots,
    clearSlots,
  };
}
